/**
 * useSupplierPriority Hook
 * Upravljanje prioritetom dobavljača
 */

import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { supplierPriorityService, SupplierPriority } from '../../../lib/supabase';

export interface UseSupplierPriorityReturn {
  // State
  priorities: SupplierPriority[];
  loading: boolean;

  // Actions
  loadPriorities: () => Promise<void>;
  movePriority: (index: number, direction: 'up' | 'down') => Promise<void>;
}

export function useSupplierPriority(): UseSupplierPriorityReturn {
  const [priorities, setPriorities] = useState<SupplierPriority[]>([]);
  const [loading, setLoading] = useState(false);

  /**
   * Učitaj prioritete iz baze
   */
  const loadPriorities = useCallback(async () => {
    try {
      const data = await supplierPriorityService.getAll();
      setPriorities(data);
    } catch (error) {
      console.error('Error loading supplier priorities:', error);
    }
  }, []);

  /**
   * Pomakni prioritet gore/dolje
   */
  const movePriority = useCallback(async (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === priorities.length - 1)
    ) {
      return;
    }

    setLoading(true);

    try {
      const newPriorities = [...priorities];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;

      // Swap
      [newPriorities[index], newPriorities[targetIndex]] =
        [newPriorities[targetIndex], newPriorities[index]];

      // Update priorities (lower number = higher priority)
      const updated = newPriorities.map((s, i) => ({
        supplier_name: s.supplier_name,
        priority: (i + 1) * 10,
      }));

      await supplierPriorityService.reorder(updated);
      await loadPriorities();
      toast.success('Prioritet ažuriran');
    } catch (error) {
      toast.error('Greška pri ažuriranju prioriteta');
    } finally {
      setLoading(false);
    }
  }, [priorities, loadPriorities]);

  return {
    priorities,
    loading,
    loadPriorities,
    movePriority,
  };
}
